import axiosInstance from 'config/axios';
import {
  Deployment,
  DeploymentJsonFile,
  DeploymentJsonFileOrNull,
  DeploymentRequirements,
  UpdateTypeLiterals,
  UserCredentials,
} from 'interfaces/deployment';
import {
  PaginationResponse,
  QueryCommonParams,
} from 'interfaces/pagination-response';

/**
 * @description Can deploy API response type
 * @type {CanDeployApiResponse}
 */
export type CanDeployApiResponse = {
  libraryHasNotChangedSinceLastDeployment: boolean;
  status: boolean;
  reasons: {
    panelLockPasscode: boolean;
    advancedSettingsPasscode: boolean;
    incompleteDrugs: boolean;
    incompleteCareArea: boolean;
  };
};

/**
 * @description Deployment service
 * @type {DeploymentService}
 */
const DeploymentService = {
  /**
   * @description Create deployment
   * @param {DeploymentRequirements} data - Deployment requirements
   * @returns {Promise<Deployment>} Deployment
   */
  create: (data: DeploymentRequirements) => {
    return axiosInstance.post<Deployment>('/v1/deployments', data);
  },

  /**
   * @description Update to full deployment
   * @param {string} deployment_id - Deployment id
   * @param {UserCredentials} credentials - User credentials
   * @returns {Promise<Deployment>} Deployment
   */
  updateToFullDeployment: (
    deployment_id: string,
    credentials: UserCredentials,
  ) => {
    return axiosInstance.put<Deployment>(
      `/v1/deployments/${deployment_id}`,
      credentials,
    );
  },

  /**
   * @description Update partial deployment
   * @param {UserCredentials} credentials - User credentials
   * @param {UpdateTypeLiterals} update_type - Update type
   * @returns {Promise<Deployment>} Deployment
   */
  updatePartialDeployment: (
    credentials: UserCredentials,
    update_type: UpdateTypeLiterals,
  ) => {
    return axiosInstance.put<Deployment>(`/v1/deployments/partial`, {
      ...credentials,
      update_type,
    });
  },

  /**
   * @description Get change report
   * @param {string | undefined} deploymentTabSelected - Deployment tab selected
   * @returns {Promise<{ oldDeployment: DeploymentJsonFileOrNull; newDeployment: DeploymentJsonFile }>} Change report
   */
  getChangeReport: (deploymentTabSelected: string | undefined) => {
    return axiosInstance.get<{
      oldDeployment: DeploymentJsonFileOrNull;
      newDeployment: DeploymentJsonFile;
    }>(`/v1/deployments/change-report`, {
      params: { deploymentTab: deploymentTabSelected },
    });
  },

  /**
   * @description Get deployments
   * @param {QueryCommonParams} queryParams - Query parameters
   * @returns {Promise<PaginationResponse<Deployment>>} Deployments
   */
  get: (queryParams: QueryCommonParams) => {
    return axiosInstance.get<PaginationResponse<Deployment>>(
      `/v1/deployments`,
      {
        params: queryParams,
      },
    );
  },

  /**
   * @description Get all active deployments
   * @returns {Promise<Deployment[]>} Active deployments
   */
  getAllActiveDeployments: () => {
    return axiosInstance.get<Deployment[]>(`/v1/deployments/active`);
  },

  /**
   * @description Get latest active deployment
   * @returns {Promise<Deployment | null>} Latest active deployment
   */
  getLatestActiveDeployment: () => {
    return axiosInstance.get<Deployment | null>(
      `/v1/deployments/latest-active`,
    );
  },

  /**
   * @description Get single deployment
   * @param {Deployment['id']} deployment_id - Deployment id
   * @returns {Promise<Deployment>} Deployment
   */
  getSingleDeployment: (deployment_id: Deployment['id']) => {
    return axiosInstance.get<Deployment>(`/v1/deployments/${deployment_id}`);
  },

  /**
   * @description Check partial deployment
   * @returns {Promise<Deployment | null>} Partial deployment
   */
  checkPartialDeployment: () => {
    return axiosInstance.get<Deployment | null>(`/v1/deployments/partial`);
  },

  /**
   * @description Can deploy
   * @param {string | undefined} deploymentTabSelected - Deployment tab selected
   * @returns {Promise<CanDeployApiResponse>} Can deploy response
   */
  canDeploy: (deploymentTabSelected: string | undefined) => {
    return axiosInstance.get<CanDeployApiResponse>(
      `/v1/deployments/can-deploy`,

      {
        params: { deploymentTab: deploymentTabSelected },
      },
    );
  },

  /**
   * @description Download the drug library report of one version of a deployment. The version
   * is required: without it the backend serves the report of the deployment's current version,
   * which is the wrong document for every older Deployment History line.
   *
   * @SADtrace SAD-003-6.6
   *
   * @History
   * 10-19-2026 aleksandar dukleski: DLA-1446 request the report of the given version
   *
   * @param {string | number} deploymentId - Deployment id
   * @param {Pick<Deployment, 'version_major' | 'version_minor'>} version - Version of the report
   * @returns {Promise<any>} Drug library report response
   */
  downloadDrugLibraryReport: (
    deploymentId: number | string,
    version: Pick<Deployment, 'version_major' | 'version_minor'>,
  ) => {
    return axiosInstance.get<any>(
      `/v1/deployments/drug-library-report/${deploymentId}/pdf`,
      {
        headers: {
          Accept: '*/*',
        },
        params: {
          version_major: version.version_major,
          version_minor: version.version_minor,
        },
        responseType: 'blob',
      },
    );
  },
};

export default DeploymentService;
